import { Card, Row, Col, Button, Form } from 'react-bootstrap';
import Select from "react-select";
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { customSelectStyles } from '../../../utils/reactSelectStyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateUserPreferences } from '../../../api/USERS/users';
import { useGetUserPreferences } from '../../../hooks/useGetUserPreferences';
import { toast } from 'react-toastify';

const PreferencesTab = ({ userId = null, isAdmin = false, userData = null }) => {
    const { user: currentUser } = useAuth();
    const queryClient = useQueryClient();

    // En mode admin, on utilise les données passées en prop ; sinon celles du user connecté
    const effectiveUser = isAdmin && userData ? userData : currentUser?.data;
    const effectiveUserId = isAdmin ? userId : currentUser?.data?.id;

    const { data: prefsData } = useGetUserPreferences(effectiveUserId);

    const [preferences, setPreferences] = useState({
        langue: null,
        fuseauHoraire: null,
        theme: null
    });

    const langueOptions = useMemo(() => [
        { value: 'fr', label: 'Français' },
        { value: 'en', label: 'English' },
        { value: 'es', label: 'Español' }
    ], []);

    const fuseauHoraireOptions = useMemo(() => [
        { value: 'Europe/Paris', label: 'Europe/Paris (UTC+1)' },
        { value: 'Europe/London', label: 'Europe/London (UTC+0)' },
        { value: 'America/New_York', label: 'America/New_York (UTC-5)' },
        { value: 'America/Los_Angeles', label: 'America/Los_Angeles (UTC-8)' },
        { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' }
    ], []);

    const themeOptions = useMemo(() => [
        { value: 'system', label: 'Automatique (selon le système)' },
        { value: 'light', label: 'Clair' },
        { value: 'dark', label: 'Sombre' }
    ], []);

    useEffect(() => {
        if (!effectiveUser) return;

        // Source de vérité pour le thème : endpoint /preferences.
        // Fallback sur user.theme si les préférences ne sont pas encore chargées.
        const themeValue = prefsData?.data?.theme ?? effectiveUser.theme ?? 'system';

        setPreferences({
            langue: langueOptions.find(opt => opt.value === 'fr'),
            fuseauHoraire: fuseauHoraireOptions.find(opt => opt.value === 'Europe/Paris'),
            theme: themeOptions.find(opt => opt.value === themeValue) ?? themeOptions[0]
        });
    }, [effectiveUser, prefsData, langueOptions, fuseauHoraireOptions, themeOptions]);

    // Appliquer le thème au DOM uniquement en mode self-edit
    // (en mode admin, on ne veut pas changer le thème de l'admin lui-même)
    useEffect(() => {
        if (isAdmin || !preferences.theme) return;

        const applyTheme = () => {
            let themeValue = preferences.theme.value;
            if (themeValue === 'system') {
                themeValue = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
            }
            document.documentElement.setAttribute("data-bs-theme", themeValue);
        };

        applyTheme();

        if (preferences.theme.value === 'system') {
            const media = window.matchMedia("(prefers-color-scheme: dark)");
            media.addEventListener("change", applyTheme);
            return () => media.removeEventListener("change", applyTheme);
        }
    }, [preferences.theme, isAdmin]);

    const updateThemeMutation = useMutation({
        mutationFn: ({ uid, theme }) =>
            updateUserPreferences(uid, { theme }),
        onSuccess: (data) => {
            // Le PUT retourne { result, message } sans data → refetch du GET pour resync
            if (data?.data) {
                queryClient.setQueryData(['userPreferences', String(effectiveUserId)], data);
            }
            queryClient.invalidateQueries({ queryKey: ['userPreferences', String(effectiveUserId)] });
            queryClient.invalidateQueries({ queryKey: ['currentUser'] });
            if (isAdmin) {
                queryClient.invalidateQueries({ queryKey: ['user', String(effectiveUserId)] });
            }
            toast.success('Préférences mises à jour avec succès');
        },
        onError: (error) => {
            toast.error(error.message || 'Erreur lors de la mise à jour des préférences');
        }
    });

    const handleSelectChange = (name, selectedOption) => {
        setPreferences(prev => ({ ...prev, [name]: selectedOption }));
    };

    const handleSubmitForm = (e) => {
        e.preventDefault();
        if (!effectiveUserId || !preferences.theme) return;
        updateThemeMutation.mutate({ uid: effectiveUserId, theme: preferences.theme.value });
    };

    return (
        <Row className='ml-3'>
            <Col xl={8}>
                <Form onSubmit={handleSubmitForm}>
                    <Card>
                        <Card.Header className="py-2 d-flex">
                            <Card.Title>
                                <span className='text-body text-s m-0 p-0'>Préférences</span>
                            </Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <Form.Group controlId='idLangue' className='mb-4'>
                                <Form.Label className='text-body text-sm'>Langue de l'interface</Form.Label>
                                <Select
                                    options={langueOptions}
                                    value={preferences.langue}
                                    onChange={(selectedOption) => handleSelectChange('langue', selectedOption)}
                                    menuPortalTarget={document.body}
                                    styles={{ ...customSelectStyles, menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    placeholder="Sélectionnez une langue"
                                    isDisabled
                                />
                                <Form.Text className="text-muted small fst-italic">
                                    La langue est actuellement fixée en français. D'autres langues seront disponibles prochainement.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group controlId='idFuseauHoraire' className='mb-4'>
                                <Form.Label className='text-body text-sm'>Fuseau horaire</Form.Label>
                                <Select
                                    options={fuseauHoraireOptions}
                                    value={preferences.fuseauHoraire}
                                    onChange={(selectedOption) => handleSelectChange('fuseauHoraire', selectedOption)}
                                    menuPortalTarget={document.body}
                                    styles={{ ...customSelectStyles, menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    placeholder="Sélectionnez un fuseau horaire"
                                    isDisabled
                                />
                                <Form.Text className="text-muted small fst-italic">
                                    Le fuseau horaire est actuellement fixé sur Europe/Paris. Cette option sera disponible prochainement.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group controlId='idTheme' className='mb-4'>
                                <Form.Label className='text-body text-sm'>Thème de l'interface</Form.Label>
                                <Select
                                    options={themeOptions}
                                    value={preferences.theme}
                                    onChange={(selectedOption) => handleSelectChange('theme', selectedOption)}
                                    menuPortalTarget={document.body}
                                    styles={{ ...customSelectStyles, menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    placeholder="Sélectionnez un thème"
                                />
                                <Form.Text className="text-muted small fst-italic">
                                    Choisissez l'apparence de l'interface. Le mode automatique s'adapte aux réglages de votre système.
                                </Form.Text>
                            </Form.Group>

                            <Button variant="primary" type="submit" disabled={updateThemeMutation.isPending}>
                                {updateThemeMutation.isPending
                                    ? <>Sauvegarde...</>
                                    : 'Sauvegarder les préférences'
                                }
                            </Button>
                        </Card.Body>
                    </Card>
                </Form>
            </Col>
        </Row>
    );
};

export default PreferencesTab;
